import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { DATE_TIME_OPERATORS, QueryOperator } from "types";
import { convertAPIOperator } from "helpers/utility/queryFilterHelpers";
import { getSelectedQueryModal } from "redux/SingleDataset/selectors";
import {
  createAndUpdateDateFilter,
  isVqlQuery,
} from "helpers/utility/vqlHelpers";
import useVqlParams from "hooks/useVqlParams";
import { useDateTimeQueryModal } from "hooks/UseQueryModal/useDateTimeQueryModal";
import BaseQueryModal from "../CustomMetadataQueryModal/BaseQueryModal";
import { DateTimeIcon } from "assets/icons/CustomMetadataIcons";
import { Dayjs } from "dayjs";
import DateTimeSelector from "../DataTimeSelector";
import {
  formatDateForAPI,
  formatDateRangeForAPI,
  parseDateFromAPI,
  parseDateRangeFromAPI,
} from "helpers/utility/customMetadataHelpers";
import { setSelectedQueryModal } from "redux/SingleDataset/actions";

interface InsertionTimeModalProps {
  editQueryId?: string;
}
export default function InsertionTimeModal({
  editQueryId,
}: InsertionTimeModalProps) {
  const dispatch = useDispatch();

  const modalRef = useRef<HTMLDivElement>(null);

  const { findVqlQuery, addVqlQuery, updateVqlQuery, vqlQueries } =
    useVqlParams();
  const selectedModalData = useSelector(getSelectedQueryModal);

  // Load existing query (if editing)
  const oldQuery = editQueryId ? findVqlQuery(editQueryId, "created_at") : null;
  const oldValue = oldQuery?.created_at?.value;
  const oldOp = oldQuery?.created_at?.op;

  const defaultOperator = oldOp
    ? convertAPIOperator(oldOp)
    : QueryOperator.GREATER_THAN;

  // operator state
  const [operator, setOperator] = useState<QueryOperator>(defaultOperator);

  // compute mode from operator
  const getMode = (op: QueryOperator): "Single" | "Range" =>
    op === QueryOperator.BETWEEN ? "Range" : "Single";

  const [mode, setMode] = useState<"Single" | "Range">(getMode(operator));

  useEffect(() => setMode(getMode(operator)), [operator]);

  // unified date value state
  const [value, setValue] = useState<{
    single: Dayjs | null;
    range: [Dayjs, Dayjs] | null;
  }>(() => ({
    single: !Array.isArray(oldValue)
      ? oldValue
        ? parseDateFromAPI(oldValue)
        : null
      : null,
    range: Array.isArray(oldValue)
      ? parseDateRangeFromAPI(oldValue as [string, string])
      : null,
  }));

  const existingQuery = useMemo(() => {
    if (editQueryId) return null;
    return (
      vqlQueries.find(
        (q) =>
          isVqlQuery(q, "created_at") &&
          q.created_at &&
          convertAPIOperator(q.created_at.op) === defaultOperator,
      ) || null
    );
  }, [vqlQueries, defaultOperator, editQueryId]);
  const submitQuery = useCallback(
    (op: QueryOperator, v: typeof value) => {
      const queryId = editQueryId || existingQuery?.id;

      if (op === QueryOperator.BETWEEN) {
        // Range mode
        if (!v.range) return;
        const [start, end] = formatDateRangeForAPI(v.range)!;
        if (!start || !end) return;

        const vqlQuery = createAndUpdateDateFilter(
          [start, end],
          op as any,
          queryId,
        );
        queryId ? updateVqlQuery(vqlQuery) : addVqlQuery(vqlQuery);
        if (selectedModalData) {
          dispatch(
            setSelectedQueryModal({ ...selectedModalData, id: vqlQuery.id }),
          );
        }
      } else {
        // Single mode
        if (!v.single) return;
        const singleValue = formatDateForAPI(v.single)!;
        if (!singleValue) return;

        const vqlQuery = createAndUpdateDateFilter(
          singleValue,
          op as any,
          queryId,
        );
        queryId ? updateVqlQuery(vqlQuery) : addVqlQuery(vqlQuery);

        if (selectedModalData) {
          dispatch(
            setSelectedQueryModal({ ...selectedModalData, id: vqlQuery.id }),
          );
        }
      }
    },
    [
      editQueryId,
      existingQuery?.id,
      selectedModalData,
      dispatch,
      updateVqlQuery,
      addVqlQuery,
    ],
  );

  const handleSubmit = () => submitQuery(operator, value);

  const isValid =
    mode === "Single"
      ? !!value.single
      : !!(value.range && value.range.length === 2);

  const { isSubmitDisabled } = useDateTimeQueryModal({
    inputValue: value,
    previousValue: oldValue,
    selectedOperator: operator,
    initialOperator: defaultOperator,
    isValid,
  });

  return (
    <BaseQueryModal<QueryOperator>
      modalRef={modalRef}
      keepOpenOnLoading
      operators={{
        options: [...DATE_TIME_OPERATORS], // spreads into a new mutable array
        selected: operator,
        onSelect: setOperator,
      }}
      onSubmit={handleSubmit}
      isSubmitDisabled={isSubmitDisabled}
      title="Date Filter"
      icon={<DateTimeIcon />}
    >
      <DateTimeSelector mode={mode} value={value} onChange={setValue} />
    </BaseQueryModal>
  );
}
